import type { StructuredResponse } from './types';
import { ClinicalMarkdownBody } from './clinicalMarkdownComponents';
import { ClinicalCallout } from './ClinicalCallout';
import { ToolSourceRow, type ToolProvenance } from './ToolSourceRow';
import { S2Card } from './S2Card';
import { AlertBanner } from './AlertBanner';
import { ResponseSection } from './ResponseSection';
import { ClinicalLensGroupCards } from './ClinicalLensGroupCards';
import { ClinicalSummaryCard } from './ClinicalSummaryCard';
import { ClinicalFactsPanel } from './ClinicalFactsPanel';
import { CodeReferencesPanel, type CodeRef } from './CodeReferencesPanel';
import { TrendVizHandoffButton } from './TrendVizHandoffButton';
import { sectionHasContent, fallbackTextDuplicatesCardContent } from './contentUtils';

/**
 * Renders normalized structured payloads instead of plain text.
 * Falls back to null (letting the caller show markdown) when structured data has no useful content.
 */
export function FinalResponseRenderer({
  structured,
  fallbackText,
  codeReferences,
}: {
  structured: StructuredResponse;
  fallbackText?: string;
  /** #83: code references for this answer, forwarded to whichever
   * ClinicalFactsPanel mount renders Section 2 so "Codes & References" can
   * render inside that same card (rule 16), never as a sibling. */
  codeReferences?: CodeRef[];
}) {
  if (!structured) return null;

  const responseType = (structured.response_type || 'flexible') as string;
  const title = structured.title;
  const subtitle = structured.subtitle;
  const callout = structured.callout;
  const sections = structured.sections ?? [];
  const presentation = structured.presentation;

  // Empty state — always render (intentional empty).
  // #89 (rule 16 "Zero-row result" edge case, settled 2026-08-07): a zero-row
  // result gets the SAME Section-2 card anatomy as a populated result (icon +
  // bold title + muted "0 rows" count + chevron), never the bespoke
  // EmptyStateCard shape (dashed border, no count, no chevron). The empty
  // message itself becomes the card's body, unchanged in wording.
  if (responseType === 'empty') {
    // #88 REOPEN (2026-08-07): this branch rendered the card and NOTHING else,
    // so the Section-1 lead-in the backend had already built for the response
    // was dropped on the floor — leaving a zero-row answer with no Section 1 at
    // all, which rule 16 requires on every response without exception.
    //
    // #88's first fix addressed the value-history route's own suppression and
    // never reached this branch, which is the same defect one route over. Every
    // other branch in this file renders the lead-in above its card; this one now
    // does too, using the same duplicate guard so a lead-in that merely repeats
    // the card's own message is still not shown twice.
    const emptyLeadIn = fallbackText?.trim();
    const showEmptyLeadIn =
      !!emptyLeadIn && !fallbackTextDuplicatesCardContent(fallbackText, sections);
    return (
      <div className="space-y-2">
        {showEmptyLeadIn && (
          <ClinicalMarkdownBody
            content={emptyLeadIn}
            className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
          />
        )}
        {title && (
          <S2Card title={title} count={0} unit="row">
            {subtitle ? (
              <p className="text-[13px] text-text-tertiary leading-relaxed">{subtitle}</p>
            ) : null}
          </S2Card>
        )}
      </div>
    );
  }

  // Alert (e.g. risk drivers unavailable)
  if (responseType === 'alert') {
    return (
      <div className="space-y-4">
        <AlertBanner
          title={title ?? 'Notice'}
          body={subtitle ?? undefined}
        />
        {callout && <ClinicalCallout callout={callout} />}
        {sections.filter(sectionHasContent).map((s, i) => (
          <ResponseSection key={i} section={s} />
        ))}
      </div>
    );
  }

  const renderableSections = sections.filter(sectionHasContent);
  const hasContent = renderableSections.length > 0 || !!callout;

  if (!hasContent) {
    return fallbackText?.trim() ? (
      <ClinicalMarkdownBody
        content={fallbackText}
        className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
      />
    ) : null;
  }

  const meta = structured.meta ?? {};

  // 3-part response layout: the short prose intro/summary (fallbackText)
  // renders on top, and the sections render inside a collapsible panel
  // instead of the normal bare section rendering below. Gated solely on this
  // meta flag — set by the chart-data pilot ("Clinical Facts" tables) and the
  // CMA report ("Facts & Reasoning" list sections via adk_facts_panel_title).
  if (meta.adk_clinical_facts_panel === true) {
    const suppressFallback = fallbackTextDuplicatesCardContent(fallbackText, renderableSections);
    // #58 (USER ORDER 2026-08-06): the prepared patient summary gets its OWN
    // collapsible, above the reasoning one. Partitioned on the section's
    // `panel` marker, which the adapter sets explicitly -- never by matching
    // section titles, which are model-authored and can drift.
    //
    // #27 (2026-08-09): that collapsible is now NESTED inside the reasoning
    // card instead of mounted beside it. Both used to be ClinicalFactsPanel
    // mounts in this bare `space-y-4` wrapper — siblings in the DOM with no
    // shared visual container — so every response rendered four top-level
    // elements (S1 lead-in, Patient Summary card, Review card, References
    // card). Rule 16 provides for three plus the 2b references sibling and
    // names "multiple flat sibling boxes at top level" an automatic FORMAT
    // FAIL; the deduction was taken on all ten queries of the Hayden round.
    //
    // #58's order is unchanged by this — it asked for two collapsibles inside
    // Section 2, and there are still two, one inside the other. What changes
    // is that "inside Section 2" is now true visually and not only in the
    // element tree.
    const summarySections = renderableSections.filter((s) => s.panel === 'patient_summary');
    const reasoningSections = renderableSections.filter((s) => s.panel !== 'patient_summary');
    const summaryTitle =
      typeof meta.adk_patient_summary_panel_title === 'string' && meta.adk_patient_summary_panel_title.trim()
        ? meta.adk_patient_summary_panel_title
        : 'Patient Summary';
    const factsTitle =
      typeof meta.adk_facts_panel_title === 'string' && meta.adk_facts_panel_title.trim()
        ? meta.adk_facts_panel_title
        : undefined;
    return (
      <div className="space-y-4">
        {fallbackText?.trim() && !suppressFallback && (
          <ClinicalMarkdownBody
            content={fallbackText}
            className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
          />
        )}
        {reasoningSections.length > 0 ? (
          <ClinicalFactsPanel
            sections={reasoningSections}
            title={factsTitle}
            /* The trend family's hand-off to the chart page. It hangs off the
               reasoning card because that is the card holding the measurements
               it plots — and a trend payload never carries a patient-summary
               group, so this branch is the one every trend card takes. The
               component itself renders nothing when the meta is absent, which
               is every non-trend response. */
            headerAction={<TrendVizHandoffButton targets={meta.adk_trend_viz_targets} />}
            nestedGroup={
              summarySections.length > 0
                ? { title: summaryTitle, sections: summarySections }
                : undefined
            }
          />
        ) : (
          // Summary-only response: it becomes the ONE Section-2 card in its own
          // right rather than an empty reasoning card wrapping it. Nesting it
          // under a "Review & Recommendations" header with nothing else in
          // there would title the card after content the response does not
          // have.
          summarySections.length > 0 && (
            <ClinicalFactsPanel sections={summarySections} title={summaryTitle} />
          )
        )}
        {/*
          USER ORDER #116 (2026-08-07): References is its OWN collapsible,
          sitting BELOW the content card and ABOVE Section 3 — never nested
          inside Clinical Facts (which is what #83 had done).

          The route gate is enforced server-side in control_plane._terminal_text,
          so anything arriving here has already been cleared to show references.
          Placement is the only decision left to the client.
        */}
        {codeReferences && codeReferences.length > 0 && (
          <CodeReferencesPanel references={codeReferences} />
        )}
      </div>
    );
  }

  const isNarrativeSummaryCard =
    meta.summary_narrative_card === true ||
    structured.response_type === 'narrative_summary_card' ||
    (structured.response_type === 'summary' && meta.render_hint === 'summary') ||
    structured.presentation?.variant === 'summary_narrative_card';
  if (isNarrativeSummaryCard) {
    // Every narrative-summary-card route gets its Section 1 lead-in, not just
    // chart_briefing_presentation (the #97 fix was scoped to one route by
    // mistake, which silently dropped the S1 sentence - e.g. the Knowledge-RAG
    // "Below is the retrieved clinical evidence..." lead-in - for every other
    // route in this family). fallbackTextDuplicatesCardContent already decides
    // this per-response from the actual text, not from a route allowlist, so
    // it is the only gate needed: a short, distinct lead-in renders, a longer
    // message that restates the card's own sections is suppressed.
    const suppressFallback = fallbackTextDuplicatesCardContent(fallbackText, renderableSections);
    return (
      <div className="space-y-4">
        {fallbackText?.trim() && !suppressFallback && (
          <ClinicalMarkdownBody
            content={fallbackText}
            className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
          />
        )}
        <ClinicalSummaryCard structured={structured} />
      </div>
    );
  }

  const toolsUsed = (meta.tools_used as string[] | undefined) ?? [];
  const provenance = meta.tool_provenance as ToolProvenance | undefined;
  const specialtyLensLabels = (meta.specialty_lens_labels as string[] | undefined) ?? [];
  const showSpecialtyLenses =
    responseType === 'specialty' && specialtyLensLabels.length > 0;
  const isMulti = responseType === 'multi' || responseType === 'flexible';
  // #89/#93: the title now renders as the shared S2Card header below (icon +
  // bold title + muted count + chevron) instead of a bespoke <h3> sitting on
  // the page background, so this remaining sub-header block only ever needs
  // to carry the subtitle / specialty-lens row that used to sit under it.
  const showSubHeader = Boolean(subtitle || showSpecialtyLenses);
  const hideToolSourceRow = meta.chart_briefing_presentation === true;
  // #88/#89/#93: this general fallthrough branch used to render Section 2
  // with NO card at all — a bare <h3> title on the page background plus a
  // loop of ResponseSections — while every other branch mounted
  // ClinicalFactsPanel (bordered card, icon + bold title + muted count +
  // collapse chevron). Rule 16 requires that same anatomy on every branch,
  // so this now mounts ClinicalFactsPanel too: `observation_value_history`,
  // `table`, `multi`, `flexible`, `specialty`, and any unknown response_type
  // all share the one card shell. The backend's deterministic, count-coherent
  // lead-in (_build_observation_value_history_text and siblings) still
  // renders above it as Section 1 via `fallbackText` below, unchanged.
  //
  // Setting metadata.adk_suppress_text_stream = False on the backend could not
  // help: no frontend code reads that flag. The gate is, and always was, this
  // component's branch structure.
  //
  // Uses the same duplicate check as its two siblings, so a genuine short
  // lead-in renders and a restatement of the card's own content is suppressed.
  const suppressFallback = fallbackTextDuplicatesCardContent(fallbackText, renderableSections);

  return (
    <div className={presentation?.density === 'compact' ? 'space-y-4' : presentation?.density === 'relaxed' ? 'space-y-7' : 'space-y-6'}>
      {fallbackText?.trim() && !suppressFallback && (
        <ClinicalMarkdownBody
          content={fallbackText}
          className="prose prose-sm dark:prose-invert max-w-none leading-relaxed"
        />
      )}
      {showSubHeader && (
        <div className="space-y-2 border-b border-border/50 pb-3">
          {showSpecialtyLenses && <ClinicalLensGroupCards labels={specialtyLensLabels} />}
          {subtitle && !showSpecialtyLenses && (
            <p className={`${presentation?.font_scale === 'sm' ? 'text-[12.5px]' : 'text-sm'} text-text-tertiary mt-0.5`}>{subtitle}</p>
          )}
        </div>
      )}

      {isMulti && toolsUsed.length > 0 && !hideToolSourceRow && (
        <ToolSourceRow tools={toolsUsed} provenance={provenance} />
      )}

      {callout && <ClinicalCallout callout={callout} />}

      <ClinicalFactsPanel sections={renderableSections} title={title} />
      {/* USER ORDER #116: own collapsible, below the content card. */}
      {codeReferences && codeReferences.length > 0 && (
        <CodeReferencesPanel references={codeReferences} />
      )}
    </div>
  );
}
